import { Col, Row, Typography, message as antMessage } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { ACCEPTED, CREATOR, DELIVERED, FAN, MESSAGE_TIME_FORMAT } from "../../helpers/constant";
import { IMessage } from "../../helpers/types";
import StatusIcon from "../../assets/icons/status-circle-black.svg";
import MessageDeleteMenu from "../MessageDeleteMenu/MessageDeleteMenu";
import MessageReadStatusIndicator from "../MessageReadStatusIndicator/MessageReadStatusIndicator";
import "./index.less";

type props = {
  message: IMessage;
  userRole?: string;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDeleteMessage?: (messageId: string) => void;
};

export const HourComponent = ({ messageTime, isRead }: { messageTime: dayjs.Dayjs; isRead: boolean }) => {
  return (
    <div
      style={{
        minWidth: "45px",
        height: "20px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        gap: "2px",
        flexShrink: 0,
      }}
    >
      {messageTime && (
        <Typography.Text className={`message-time`} style={{ paddingLeft: "8px", marginBottom: "1px", whiteSpace: "nowrap" }}>
          {messageTime.format(MESSAGE_TIME_FORMAT)}
        </Typography.Text>
      )}
      <MessageReadStatusIndicator isRead={isRead || false} />
    </div>
  );
};

const MessageTextItemComponent: React.FC<props> = ({ message, userRole, canDelete = false, isDeleting = false, onDeleteMessage }) => {
  const { t } = useTranslation();
  const messageTime = dayjs(message.createdAt);

  const handleCopy = async () => {
    const text = message.text || "";
    if (!text) return;

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      antMessage.success(t("message-copied"));
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    antMessage.success(t("message-copied"));
  };

  const condition: boolean =
    (userRole === FAN && message?.status !== ACCEPTED) || ![ACCEPTED, DELIVERED].includes(message.status!) || userRole === CREATOR;

  const messageContent = (
    <div
      className="messageTextItem"
      style={{
        display: "flex",
        flexDirection: condition ? "row" : "column",
        alignItems: "center",
      }}
    >
      {message?._id && onDeleteMessage && (
        <div className="message-top-action">
          <MessageDeleteMenu
            canDelete={canDelete}
            isDeleting={isDeleting}
            onDelete={() => onDeleteMessage(message._id!)}
            showCopy={true}
            copyDisabled={!Boolean(message.text?.trim())}
            onCopy={handleCopy}
          />
        </div>
      )}

      <Row style={{ width: "100%" }}>
        <Col span={24}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              width: "100%",
            }}
          >
            <Typography.Text className="send-message-text">{message.text}</Typography.Text>
          </div>
        </Col>
      </Row>

      <Row
        style={{
          width: `${[ACCEPTED, DELIVERED].includes(message.status!) && userRole !== CREATOR ? "100%" : "fit-content"}`,
          alignSelf: "flex-end",
          alignItems: "center",
        }}
      >
        <Col
          style={{
            width: "100%",
            display: "flex",
            justifyContent: [ACCEPTED, DELIVERED].includes(message.status!) && userRole !== CREATOR ? "space-between" : "flex-end",
            alignItems: "center",
          }}
        >
          {[ACCEPTED, DELIVERED].includes(message.status!) && userRole !== CREATOR && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img src={StatusIcon} alt={message.status} />
              <Typography.Text className="font-14-bold text-black-color pl-4">{t(message.status!)}</Typography.Text>
            </div>
          )}

          <div className="message-text-actions">
            <HourComponent messageTime={messageTime} isRead={message.isRead || false} />
          </div>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      {message?._id && onDeleteMessage ? (
        <MessageDeleteMenu
          canDelete={canDelete}
          isDeleting={isDeleting}
          onDelete={() => onDeleteMessage(message._id!)}
          showCopy={true}
          copyDisabled={!Boolean(message.text?.trim())}
          onCopy={handleCopy}
          enableLongPress={true}
        >
          {messageContent}
        </MessageDeleteMenu>
      ) : (
        messageContent
      )}
    </>
  );
};

export default MessageTextItemComponent;
